import { useEffect, useId, useRef, useState } from 'react';
import { Copy, MessageCircle, Mail, Check, ChevronDown, ChevronUp, Share2 } from 'lucide-react';
import { Button } from './ui/Button';
import {
  buildLoginErrorReport,
  buildLoginMailtoUrl,
  buildLoginWhatsAppUrl,
  copyTextToClipboard,
  shareLoginReport,
} from '../utils/loginErrorReport';
import { openExternalUrl } from '../utils/openExternalUrl';

/**
 * Compact login failure + one-tap report (WhatsApp primary).
 * Password is never included.
 */
export function LoginErrorReport({
  labels,
  role,
  username,
  errorMessage,
  errorCode,
  httpStatus,
  axiosCode,
  rawMessage,
  useMalayalam,
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const rootRef = useRef(null);
  const detailsId = useId();

  const report = buildLoginErrorReport({
    role,
    username,
    errorMessage,
    errorCode,
    httpStatus,
    axiosCode,
    rawMessage,
    useMalayalam,
  });

  useEffect(() => {
    if (!errorMessage || !rootRef.current) return;
    try {
      rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch {
      // ignore
    }
  }, [errorMessage, errorCode, httpStatus]);

  if (!errorMessage) return null;

  const onCopy = async () => {
    setCopyFailed(false);
    const ok = await copyTextToClipboard(report);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } else {
      setCopyFailed(true);
      setShowDetails(true);
    }
  };

  const onWhatsApp = () => {
    openExternalUrl(buildLoginWhatsAppUrl(report));
  };

  const onEmail = () => {
    openExternalUrl(buildLoginMailtoUrl(report));
  };

  const onShare = async () => {
    setShareBusy(true);
    try {
      const ok = await shareLoginReport(
        report,
        labels.loginReportTitle || 'NMhelper login error report'
      );
      if (!ok) await onCopy();
    } finally {
      setShareBusy(false);
    }
  };

  const iconBtn =
    'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-text transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:opacity-50';

  return (
    <div
      ref={rootRef}
      role="alert"
      className="overflow-hidden rounded-lg border border-danger/25 bg-danger-bg/60"
    >
      {/* Error message — quiet, readable */}
      <div className="border-l-[3px] border-danger px-3 py-2.5 sm:px-3.5">
        <p className="text-sm leading-snug text-danger whitespace-pre-wrap">{errorMessage}</p>
      </div>

      {/* Report row — WhatsApp primary + icon secondary */}
      <div className="border-t border-danger/15 bg-card/70 px-3 py-2.5 sm:px-3.5 space-y-2">
        <p className="text-[11px] leading-snug text-muted">
          {labels.loginReportHintShort ||
            labels.loginReportHint ||
            'Need help? Send a report (password not included).'}
        </p>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={onWhatsApp}
            className="min-h-10 flex-1 gap-1.5 !bg-[#25D366] !text-white hover:!bg-[#1da851] border-transparent shadow-none"
          >
            <MessageCircle className="h-4 w-4 shrink-0" aria-hidden />
            <span className="truncate">
              {labels.loginReportWhatsAppShort || labels.loginReportWhatsApp || 'WhatsApp'}
            </span>
          </Button>

          <button
            type="button"
            onClick={onCopy}
            className={iconBtn}
            title={
              copied
                ? labels.loginReportCopiedShort || 'Copied'
                : labels.loginReportCopyShort || 'Copy'
            }
            aria-label={
              copied
                ? labels.loginReportCopiedShort || 'Copied'
                : labels.loginReportCopy || 'Copy full report'
            }
          >
            {copied ? (
              <Check className="h-4 w-4 text-success" aria-hidden />
            ) : (
              <Copy className="h-4 w-4" aria-hidden />
            )}
          </button>

          <button
            type="button"
            onClick={onShare}
            disabled={shareBusy}
            className={iconBtn}
            title={labels.loginReportShare || 'Share'}
            aria-label={labels.loginReportShare || 'Share report'}
          >
            <Share2 className={`h-4 w-4 ${shareBusy ? 'animate-pulse' : ''}`} aria-hidden />
          </button>

          <button
            type="button"
            onClick={onEmail}
            className={iconBtn}
            title={labels.loginReportEmail || labels.contactEmail || 'Email'}
            aria-label={labels.loginReportEmail || labels.contactEmail || 'Email report'}
          >
            <Mail className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {copied && (
          <p className="text-[11px] text-success">
            {labels.loginReportCopied || 'Copied — paste anywhere'}
          </p>
        )}
        {copyFailed && (
          <p className="text-[11px] text-warning">
            {labels.loginReportCopyFailed ||
              'Copy failed — open details and long-press to select.'}
          </p>
        )}

        <button
          type="button"
          className="flex items-center gap-0.5 text-[11px] font-medium text-muted hover:text-primary"
          onClick={() => setShowDetails((v) => !v)}
          aria-expanded={showDetails}
          aria-controls={detailsId}
        >
          {showDetails ? (
            <ChevronUp className="h-3 w-3" aria-hidden />
          ) : (
            <ChevronDown className="h-3 w-3" aria-hidden />
          )}
          {showDetails
            ? labels.loginReportHide || 'Hide details'
            : labels.loginReportShow || 'Show report details'}
        </button>

        {showDetails && (
          <pre
            id={detailsId}
            className="max-h-36 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-surface px-2 py-1.5 text-[10px] leading-snug text-muted select-all"
          >
            {report}
          </pre>
        )}
      </div>
    </div>
  );
}
